/*
* === AcademicProgressPage.js (Final - Enterprise Payload & Preview Fixes) ===
*/
import React, { useEffect, useState, useCallback } from 'react';
import {
  Container, Typography, Box, Alert, Button, Grid, Paper,
  CircularProgress, FormControl, InputLabel, Select, MenuItem,
  TextField, Link, Chip, IconButton, Dialog, DialogTitle, DialogContent,
  LinearProgress, useTheme
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

// Hardcode production API fallback to prevent localhost preview errors
const API_BASE = 'https://brightsparc-api-520983012715.africa-south1.run.app';

const AcademicProgressPage = () => {
  const theme = useTheme(); 
  const [fundedApplications, setFundedApplications] = useState([]);
  const [progressReports, setProgressReports] = useState([]);
  const [selectedAppId, setSelectedAppId] = useState('');
  const [termName, setTermName] = useState('');
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);
  const { token } = useAuth();

  const fetchPageData = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError('');

      const appRes = await api.get('/applicant/dashboard', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const funded = appRes.data.filter(
        app => app.Status === 'Funded' || app.Status === 'Approved'
      );
      setFundedApplications(funded);
      if (funded.length > 0 && !selectedAppId) {
        setSelectedAppId(funded[0].Id);
      }

      const progressRes = await api.get('/applicant/progress', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProgressReports(progressRes.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load page data.');
    } finally {
      setLoading(false);
    }
  }, [token, selectedAppId]);

  useEffect(() => {
    fetchPageData();
  }, [fetchPageData]);

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file || !selectedAppId || !termName) {
      setError('Please select a bursary, term name, and file.');
      return;
    }
    const formData = new FormData();
    
    // 🚨 FIX: Send ONLY the specific key that your backend Multer expects. 
    // (If your backend still throws the error, change 'document' to 'file' or 'attachment' based on what your applicantRoutes.js says)
    formData.append('document', file); 
    
    formData.append('applicationId', selectedAppId);
    formData.append('termName', termName);

    setUploading(true);
    setError('');
    try {
      await api.post('/applicant/progress', formData, {
        headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
        }
      });
      setFile(null);
      setTermName('');
      const fileInput = document.getElementById('file-input');
      if(fileInput) fileInput.value = '';
      
      fetchPageData();
    } catch (err) {
      setError(err.response?.data?.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handlePreview = (e, uploadPath) => {
    e.stopPropagation(); 
    if (!uploadPath || typeof uploadPath !== 'string') return;
    
    // 🚨 FIX: Smart URL construction for production vs local
    const cleanPath = uploadPath.replace(/\\/g, '/');
    const url = uploadPath.startsWith('http') ? uploadPath : `${API_BASE}/${cleanPath}`;
    
    setPreviewUrl(url);
    setPreviewOpen(true);
  };

  const handleDownloadAll = async () => {
    const pdfReports = progressReports.filter(r => r.UploadPath && r.FileName?.toLowerCase().endsWith('.pdf'));

    if (pdfReports.length === 0) {
      setError('No PDF files to download.');
      return;
    }

    setIsDownloading(true);
    setDownloadProgress(0);
    setError('');

    const zip = new JSZip();
    const folder = zip.folder('academic-progress-reports');
    let completed = 0;

    try {
      for (const report of pdfReports) {
        const cleanPath = report.UploadPath.replace(/\\/g, '/');
        const url = report.UploadPath.startsWith('http') ? report.UploadPath : `${API_BASE}/${cleanPath}`;

        const response = await fetch(url);
        if (response.ok) {
          const blob = await response.blob();
          folder.file(report.FileName, blob);
        }
        completed++;
        setDownloadProgress(Math.round((completed / pdfReports.length) * 100));
      }

      const content = await zip.generateAsync({ type: 'blob' });
      saveAs(content, 'Academic_Progress_Reports.zip');
    } catch (err) {
      setError('Failed to download files.');
    } finally {
      setIsDownloading(false);
      setDownloadProgress(0);
    }
  };

  const columns = [
    { field: 'TermName', headerName: 'Term', width: 200 },
    {
      field: 'SubmittedAt',
      headerName: 'Submitted On',
      width: 180,
      valueFormatter: (params) => params.value ? new Date(params.value).toLocaleString('en-ZA') : 'N/A'
    },
    {
      field: 'Status',
      headerName: 'Status',
      width: 130,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={params.value === 'Verified' ? 'success' : params.value === 'Rejected' ? 'error' : 'default'}
          size="small"
        />
      )
    },
    {
      field: 'FileName',
      headerName: 'Document',
      minWidth: 280,
      flex: 1,
      renderCell: (params) => {
        // 🚨 FIX: Failsafe data mapping in case the backend returns slightly different object properties
        const uploadPath = params.row.UploadPath || params.row.uploadPath || params.row.DocumentPath || params.row.document?.UploadPath;
        const fileName = params.row.FileName || params.row.fileName || params.row.DocumentName || params.row.document?.FileName || 'Academic Record';

        if (!uploadPath) {
          return <span style={{ color: '#999', fontStyle: 'italic' }}>No File / Syncing</span>;
        }

        const cleanPath = uploadPath.replace(/\\/g, '/');
        const fileUrl = uploadPath.startsWith('http') ? uploadPath : `${API_BASE}/${cleanPath}`;
        const isPdf = fileName.toLowerCase().endsWith('.pdf');

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <Link href={fileUrl} target="_blank" rel="noopener noreferrer" underline="hover" sx={{ flexGrow: 1, wordBreak: 'break-word' }}>
              {fileName}
            </Link>
            {isPdf && (
              <IconButton size="small" color="primary" onClick={(e) => handlePreview(e, uploadPath)}>
                <VisibilityIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        );
      }
    },
    {
      field: 'AdminNotes',
      headerName: 'Admin Feedback',
      minWidth: 300,
      flex: 2,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.4 }}>
          {params.value || '—'}
        </Typography>
      )
    }
  ];

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;

  return (
    <>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>My Academic Progress</Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Submit your academic results here for each term to maintain your bursary funding.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {fundedApplications.length === 0 ? (
          <Alert severity="info">You do not have any funded bursaries to submit progress reports for.</Alert>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Paper component="form" onSubmit={handleSubmit} sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>Submit New Report</Typography>

                <FormControl fullWidth margin="normal">
                  <InputLabel>Select Bursary</InputLabel>
                  <Select value={selectedAppId} label="Select Bursary" onChange={(e) => setSelectedAppId(e.target.value)}>
                    {fundedApplications.map(app => <MenuItem key={app.Id} value={app.Id}>{app.FundName}</MenuItem>)}
                  </Select>
                </FormControl>

                <TextField fullWidth margin="normal" label="Term Name (e.g., 2026 Semester 1)" value={termName} onChange={(e) => setTermName(e.target.value)} />

                <Button variant="outlined" component="label" fullWidth sx={{ mt: 1, mb: 2 }} startIcon={file ? <CheckCircleIcon color="success" /> : <UploadFileIcon />}>
                  {file ? file.name : 'Choose Results File'}
                  <input id="file-input" type="file" hidden onChange={handleFileChange} accept=".pdf,.jpeg,.jpg,.png" />
                </Button>

                <Button type="submit" variant="contained" fullWidth disabled={uploading}>
                  {uploading ? <CircularProgress size={24} color="inherit" /> : 'Submit Progress'}
                </Button>
              </Paper>
            </Grid>

            <Grid item xs={12} md={8}>
              <Paper sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">My Submission History</Typography>
                  <Box sx={{ minWidth: 180 }}>
                    <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={handleDownloadAll} disabled={isDownloading || progressReports.filter(r => r.UploadPath).length === 0} fullWidth>
                      {isDownloading ? `Downloading... ${downloadProgress}%` : 'Download All PDF'}
                    </Button>
                    {isDownloading && <LinearProgress variant="determinate" value={downloadProgress} sx={{ mt: 1, borderRadius: 1 }} />}
                  </Box>
                </Box>

                <Box sx={{ height: 500, width: '100%' }}>
                  <DataGrid
                    rows={progressReports}
                    columns={columns}
                    getRowId={(row) => row.Id}
                    density="compact"
                    disableColumnMenu
                    autoHeight={false}
                    sx={{
                      '& .MuiDataGrid-cell': { whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: '1.4 !important', py: 1.5, color: theme.palette.text.primary, borderBottom: `1px solid ${theme.palette.divider}` },
                      '& .MuiDataGrid-columnHeaders': { backgroundColor: theme.palette.mode === 'dark' ? theme.palette.action.hover : '#f5f5f5', color: theme.palette.text.primary, fontWeight: 'bold', borderBottom: `2px solid ${theme.palette.divider}` },
                      '& .MuiDataGrid-footerContainer': { borderTop: `1px solid ${theme.palette.divider}` }
                    }}
                  />
                </Box>
              </Paper>
            </Grid>
          </Grid>
        )}
      </Container>

      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Document Preview
          <IconButton onClick={() => setPreviewOpen(false)} sx={{ color: (theme) => theme.palette.grey[500] }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, height: '80vh', bgcolor: '#1e1e1e' }}>
          {previewUrl && <iframe key={previewUrl} src={previewUrl} title="Preview" width="100%" height="100%" style={{ border: 'none' }} />}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AcademicProgressPage;